import React, {
  ChangeEvent,
  FocusEventHandler,
  FunctionComponent,
  useCallback,
  useRef,
} from 'react'

import { Flex, TextInput } from '../../atoms'
import { useNotifyingState } from '../../extensions'
import { isValidNumber, isValidPartialYearString } from '../../validators'
import { DateInputs } from './types'

export * from './mappers'
export * from './types'

export interface DateInputProps {
  dateInputs: DateInputs
  onBlur?: FocusEventHandler
  onChange: (inputs: DateInputs) => void
  placeholders?: DateInputs
}

export const DateInput: FunctionComponent<DateInputProps> = ({
  dateInputs,
  onBlur,
  onChange,
  placeholders,
}) => {
  const [inputs, setInputs] = useNotifyingState(dateInputs, [onChange])

  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  const handleBlur = useCallback<FocusEventHandler>(
    (event) => {
      if (!['day', 'month', 'year'].includes(event.relatedTarget?.id ?? '')) {
        onBlur?.(event)
      }
    },
    [onBlur],
  )

  const onDayChanged = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      if (isValidNumber(target.value)) {
        setInputs({ ...inputs, day: target.value.substring(0, 2) })

        if (target.value.length >= 2) {
          monthRef.current?.focus()
        }
      }
    },
    [inputs, setInputs],
  )

  const onMonthChanged = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      if (isValidNumber(target.value)) {
        setInputs({ ...inputs, month: target.value.substring(0, 2) })

        if (target.value.length >= 2) {
          yearRef.current?.focus()
        }
      }
    },
    [inputs, setInputs],
  )

  const onYearChanged = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      if (isValidPartialYearString(target.value)) {
        setInputs({ ...inputs, year: target.value.substring(0, 4) })
      }
    },
    [inputs, setInputs],
  )

  return (
    <Flex gap={3} width={[1, 1, 1, 3 / 5]} justifyContent='flex-start'>
      <TextInput
        width='28%'
        type='tel'
        id='day'
        name='day'
        placeholder={placeholders?.day}
        value={inputs.day || ''}
        onBlur={handleBlur}
        onChange={onDayChanged}
        ref={dayRef}
      />
      <TextInput
        width='28%'
        type='tel'
        id='month'
        name='month'
        placeholder={placeholders?.month}
        value={inputs.month || ''}
        onBlur={handleBlur}
        onChange={onMonthChanged}
        ref={monthRef}
      />
      <TextInput
        width='44%'
        type='tel'
        id='year'
        name='year'
        placeholder={placeholders?.year}
        value={inputs.year || ''}
        onBlur={handleBlur}
        onChange={onYearChanged}
        ref={yearRef}
      />
    </Flex>
  )
}
